/**
 * PoolCard Component - Phase 2C-2 (PROPERLY FIXED)
 * 
 * Displays pool information in a card format.
 * 
 * FIXED: Wraps Card in clickable div (Card doesn't support onClick)
 * FIXED: Removed fullWidth prop from Button (not supported)
 * FIXED: Removed unused getModeLabel import
 */

import { useNavigate } from 'react-router-dom';
import { Pool, PoolMembership } from '../../types';
import { Card, CardContent, Button } from '../common';
import { ModeBadge, StatusBadge } from '../common';
import { formatDate, getDomainLabel } from '../../utils';

interface PoolCardProps {
  pool: Pool;
  membership?: PoolMembership | null;
  onJoin?: () => void;
  compact?: boolean;
  className?: string;
}

export function PoolCard({
  pool,
  membership,
  onJoin,
  compact = false,
  className = '',
}: PoolCardProps): JSX.Element {
  const navigate = useNavigate();
  const isMember = !!membership;
  const isFull = pool.max_participants 
    ? (pool.participant_count ?? 0) >= pool.max_participants 
    : false;
  const canJoin = !isMember && pool.status === 'active' && !isFull;

  const handleViewPool = () => {
    navigate(`/pools/${pool.pool_id}`);
  };

  if (compact) {
    return (
      <div 
        onClick={handleViewPool}
        className={`cursor-pointer hover:bg-slate-800/50 transition-colors ${className}`}
      >
        <Card>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ModeBadge mode={pool.mode} size="sm" />
                <span className="font-medium text-slate-100">{pool.pool_name}</span>
                <StatusBadge status={pool.status} size="sm" />
              </div>
              {membership && (
                <span className="font-mono text-slate-300">
                  {membership.current_balance.toLocaleString()}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      onClick={handleViewPool}
      className={`cursor-pointer ${className}`}
    >
      <Card className="hover:border-slate-600 transition-colors">
        <CardContent>
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-semibold text-slate-100">{pool.pool_name}</h3>
              <p className="text-sm text-slate-400">{getDomainLabel(pool.domain)}</p>
            </div>
            <div className="flex gap-2">
              <ModeBadge mode={pool.mode} />
              <StatusBadge status={pool.status} />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
            <div>
              <span className="text-slate-400">Bankroll</span>
              <p className="font-mono text-slate-100">{pool.initial_bankroll.toLocaleString()}</p>
            </div>
            <div>
              <span className="text-slate-400">Participants</span>
              <p className="text-slate-100">
                {pool.participant_count ?? 0}
                {pool.max_participants && ` / ${pool.max_participants}`}
              </p>
            </div>
            <div>
              <span className="text-slate-400">Start</span>
              <p className="text-slate-100">{formatDate(pool.start_date)}</p>
            </div>
            <div>
              <span className="text-slate-400">End</span>
              <p className="text-slate-100">{formatDate(pool.end_date)}</p>
            </div>
          </div>

          {/* Membership Info */}
          {membership && (
            <div className="pt-4 border-t border-slate-700">
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Your Balance</span>
                <span className="font-mono text-slate-100">
                  {membership.current_balance.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-slate-400">Record</span>
                <span className="text-slate-100">
                  {membership.predictions_won ?? membership.predictions_correct ?? 0}W - {membership.predictions_lost ?? 0}L
                </span>
              </div>
            </div>
          )}

          {/* Actions */}
          {canJoin && onJoin && (
            <div className="mt-4" onClick={(e) => e.stopPropagation()}>
              <Button 
                variant="primary" 
                size="sm" 
                onClick={onJoin}
                className="w-full"
              >
                Join Pool
              </Button>
            </div>
          )}

          {isFull && !isMember && (
            <div className="mt-4 text-center text-sm text-slate-500">
              Pool is full
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
